<script setup lang="ts">
// Background queue strip shown above the main dock button: the ticket waiting
// in the other lane (a 5v5 search during a 1v1 match) and the queue that can
// be added right now. The main button keeps owning the main activity.
import { Plus, X } from '@lucide/vue'

defineProps<{
  background: { queueID: string; label: string; elapsed: string }[]
  offers: { queueID: string; label: string }[]
  busy: boolean
  canCancel: boolean
}>()

const emit = defineEmits<{
  join: [queueID: string]
  leave: [queueID: string]
}>()
</script>

<template>
  <div class="queue-lanes" role="group" aria-label="Параллельный поиск">
    <div
      v-for="ticket in background"
      :key="ticket.queueID"
      class="queue-lane-ticket"
      role="status"
    >
      <span class="queue-lane-text">
        <span class="queue-lane-caption">Также ищем</span>
        <strong>{{ ticket.label }}</strong>
      </span>
      <time class="queue-lane-time">{{ ticket.elapsed }}</time>
      <button
        class="queue-lane-cancel"
        type="button"
        :disabled="busy || !canCancel"
        :aria-label="`Отменить поиск: ${ticket.label}`"
        :title="canCancel ? `Отменить поиск: ${ticket.label}` : 'Отменить поиск группы может только лидер'"
        @click="emit('leave', ticket.queueID)"
      >
        <X aria-hidden="true" />
      </button>
    </div>
    <button
      v-for="offer in offers"
      :key="offer.queueID"
      class="queue-lane-offer"
      type="button"
      :disabled="busy"
      :title="`Встать в очередь ${offer.label}, не прерывая текущую игру или поиск`"
      @click="emit('join', offer.queueID)"
    >
      <Plus aria-hidden="true" />
      <span>Также искать {{ offer.label }}</span>
    </button>
  </div>
</template>

<style scoped>
.queue-lanes {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  display: grid;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
}

.queue-lane-ticket,
.queue-lane-offer {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  box-sizing: border-box;
  padding: 4px 4px 4px 12px;
  border: 1px solid rgb(255 255 255 / 0.12);
  background: rgb(10 13 12 / 0.92);
  box-shadow: 0 6px 18px rgb(0 0 0 / 0.42), inset 0 1px 0 rgb(255 255 255 / 0.06);
  color: #e6ecea;
  font-size: 12px;
  line-height: 1.2;
}

.queue-lane-text {
  display: grid;
  flex: 1;
  min-width: 0;
}

.queue-lane-caption {
  color: #93a09d;
  font-size: 12px;
}

.queue-lane-text strong {
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-lane-time {
  color: #c8d2d0;
  font-variant-numeric: tabular-nums;
}

.queue-lane-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #93a09d;
  cursor: pointer;
}

.queue-lane-cancel svg,
.queue-lane-offer svg {
  width: 16px;
  height: 16px;
  flex: none;
}

.queue-lane-cancel:hover:not(:disabled) {
  background: rgb(255 255 255 / 0.08);
  color: #fff;
}

.queue-lane-offer {
  padding-right: 12px;
  color: #c8d2d0;
  cursor: pointer;
  text-align: left;
}

.queue-lane-offer span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-lane-offer:hover:not(:disabled) {
  border-color: rgb(255 255 255 / 0.28);
  color: #fff;
}

.queue-lane-cancel:disabled,
.queue-lane-offer:disabled {
  cursor: default;
  opacity: 0.5;
}
</style>
